'use client';

import { ContextMenu, Flex, Spinner, Text } from "@radix-ui/themes";
import { AnimatePresence, motion } from "motion/react";
import moment from "moment";
import { AiOutlineDelete } from "react-icons/ai";
import { LuCopy, LuReplyAll } from "react-icons/lu";
import { toast } from "sonner";
import { MessageType } from "./hook/useLiveChat";
import { useEffect, useRef, useState } from "react";
import useLayout from "@/components/hooks/useLayout";

interface ChatBubbleType {
  data: MessageType;
  replyToComponent: any;
  index?: number;
  onDelete?: () => void;
  onReply?: (msg: MessageType) => void;
  viewerRole?: "admin" | "user";
  counterpartName?: string;
}

export const ChatBubble = ({
  data,
  replyToComponent,
  onDelete,
  onReply,
  viewerRole = "user",
  counterpartName = "User",
}: ChatBubbleType) => {
  const isOwnMessage = data.from === viewerRole;
  const date = data.created_at ? new Date(data.created_at) : undefined;
  const formattedTime = date ? moment(date).format("HH:mm") : "--:--";
  const { isMobile } = useLayout();

  const [x, setX] = useState(0);
  const isDragged = useRef(false);

  const userOffset = 100;
  const adminOffset = -100;

  const handleDragEnd = () => {
    if (isDragged.current) {
      setX(0);
      isDragged.current = false;
    }
  };

  useEffect(() => {
    if (isDragged.current) {
      if (isOwnMessage && x > userOffset) {
        onReply?.(data);
      } else if (!isOwnMessage && x < adminOffset) {
        onReply?.(data);
      }
    }
  }, [x, data, isOwnMessage, onReply]);

  return (
    <div
      id={data.id}
      className={`flex ${isOwnMessage ? "justify-end" : ""} items-center relative`}
    >
      {isOwnMessage && x > userOffset && (
        <motion.div
          initial={{ x: -20 }}
          animate={{ x: 0 }}
          transition={{ duration: 0.3 }}
          exit={{ x: 20 }}
          className="absolute z-[-2] mr-[15px]"
        >
          <LuReplyAll />
        </motion.div>
      )}

      <ContextMenu.Root>
        <ContextMenu.Trigger>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            dragMomentum={false}
            dragTransition={{ bounceStiffness: 200, bounceDamping: 20 }}
            dragSnapToOrigin
            dragElastic={isMobile ? 0.6 : 0.7}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            drag="x"
            dragDirectionLock={true}
            dragConstraints={{
              left: isOwnMessage ? -400 : 0,
              right: isOwnMessage ? 0 : 400,
            }}
            style={{ x }}
            onDrag={(_, info) => {
              isDragged.current = true;
              setX(info.offset.x);
            }}
            onDragEnd={handleDragEnd}
            className={`
              w-fit px-0 pb-2
              border
              select-none
              dark:border-card-border-dark
              border-card-border-light
              rounded-md
              max-w-[80%]
              ${isOwnMessage && "rounded-tr-none bg-[--accent-3]"}
              ${!isOwnMessage && "rounded-tl-none bg-[--gray-2]"}
            `}
          >
            {replyToComponent}
            <div className="px-2">
              <Text
                trim={"end"}
                className="text-left capitalize text-primary-500 text-[10px]"
              >
                {data.from === viewerRole
                  ? "You"
                  : viewerRole === "admin"
                  ? counterpartName
                  : "Admin"}
              </Text>
              <Flex align={"end"}>
                <Text className="text-left text-[14px]">{data.text}</Text>
                <Text
                  trim={"both"}
                  className="ml-3 flex items-center gap-1 text-right text-[10px]"
                  color="gray"
                >
                  {formattedTime}
                  {viewerRole === "admin" &&
                    isOwnMessage &&
                    data.readByUser && <span title="Read by user">Seen</span>}
                  {data.created_at == undefined && <Spinner size={"1"} />}
                </Text>
              </Flex>
            </div>
          </motion.div>
        </ContextMenu.Trigger>
        <ContextMenu.Content>
          <ContextMenu.Item onClick={() => onReply?.(data)}>
            <LuReplyAll /> Reply
          </ContextMenu.Item>
          <ContextMenu.Item
            onClick={() => {
              navigator.clipboard.writeText(data.text ?? "");
              toast.info("Copied");
            }}
          >
            <LuCopy /> Copy
          </ContextMenu.Item>
          <ContextMenu.Separator />
          <ContextMenu.Item onClick={onDelete} color="red">
            <AiOutlineDelete /> Delete
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Root>
      <AnimatePresence>
        {!isOwnMessage && x < adminOffset && (
          <motion.div
            initial={{ x: 20 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.3 }}
            exit={{ x: 20 }}
            className="absolute z-[-2] ml-[15px]"
          >
            <LuReplyAll />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export function ReplyToBubble({
  replyTo,
  className,
  viewerRole = "user",
  counterpartName = "User",
}: {
  replyTo: MessageType;
  className?: any;
  viewerRole?: "admin" | "user";
  counterpartName?: string;
}) {
  return (
    <div
      className={`
        px-2 pb-1 pt-1
        border
        select-none
        flex flex-grow
        dark:border-card-border-dark
        border-card-border-light
        rounded-md
        border-l-4 !border-l-blue-500
        text-[12px]
        transition-all
        rounded-tl-none bg-[--gray-2] flex-col italic ${className}
      `}
      onClick={() => {
        const element = document.getElementById(replyTo.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.classList.add("animate-pulse");
          element.classList.add("bg-[var(--gray-a4)]");
          setTimeout(() => {
            element.classList.remove("animate-pulse");
            element.classList.remove("bg-[var(--gray-a4)]");
          }, 1000);
        }
      }}
    >
      <Text
        trim={"normal"}
        className="text-left capitalize text-primary-500 text-[10px]"
      >
        {replyTo.from === viewerRole
          ? "You"
          : viewerRole === "admin"
          ? counterpartName
          : "Admin"}
      </Text>
      <Text color="gray" className="text-left text-[12px]">
        {replyTo.text}
      </Text>
    </div>
  );
}